import { useEffect, useState } from "react";
import { Container, OffcanvasBody, OffcanvasHeader } from "reactstrap";
import styles from "./style.module.scss";
import DefaultOffCanvas, { DefaultOffCanvasProps } from "../DefaultOffCanvas";
import { OpenClientsReportPubSub, OpenClientsReportPubSubPayload } from "@utils/pubsubs/PubSub";
import EntityReportForm, { ReportColumn } from "@components/forms/EntityReportForm";
import { ClientApi, ClientDataType } from "@/api/ClientApi";
import { ZoneApi, ZoneDataType } from "@/api/ZoneApi";
import { SalemanApi, SalemanDataType } from "@/api/SalemenApi";

type ClientsReportOffCanvasProps = {} & DefaultOffCanvasProps;

const clientColumns: ReportColumn<ClientDataType>[] = [
  { key: "codigo", header: "Código", value: (c) => c.codigo },
  { key: "nombre", header: "Nombre", value: (c) => c.nombre },
  { key: "nombre_comercial", header: "Nombre Comercial", value: (c) => c.nombre_comercial },
  { key: "zoneCode", header: "Código Zona", value: (c) => c.zone?.codigo },
  { key: "zoneName", header: "Nombre Zona", value: (c) => c.zone?.nombre },
  { key: "salemanCode", header: "Código Vendedor", value: (c) => c.saleman?.codigo },
  { key: "salemanName", header: "Nombre Vendedor", value: (c) => c.saleman?.nombre },
  { key: "tipo_identificacion", header: "Tipo Identificación", value: (c) => c.tipo_identificacion },
  { key: "identificacion", header: "Identificación", value: (c) => c.identificacion },
  { key: "email", header: "Email", value: (c) => c.email },
  { key: "telefono1", header: "Teléfono 1", value: (c) => c.telefono1 },
  { key: "telefono2", header: "Teléfono 2", value: (c) => c.telefono2 },
  { key: "direccion", header: "Dirección", value: (c) => c.direccion },
  { key: "contacto", header: "Contacto", value: (c) => c.contacto },
  { key: "cuentas_bancarias", header: "Cuentas Bancarias", value: (c) => c.cuentas_bancarias },
  { key: "plazo", header: "Plazo", value: (c) => c.plazo },
  { key: "descuento", header: "Descuento", value: (c) => c.descuento },
  { key: "credit_limit", header: "Límite de Crédito", value: (c) => c.credit_limit },
  { key: "activitycode", header: "Código Actividad", value: (c) => c.activitycode },
  { key: "active", header: "Activo", value: (c) => (c.active ? "Sí" : "No") },
];

const loadAllZones = async (): Promise<Map<string, ZoneDataType>> => {
  const zonesById = new Map<string, ZoneDataType>();
  let token: string | undefined = undefined;
  do {
    const { items, nextToken } = await ZoneApi.fetchAll({}, token, 1000, null, { noCache: true });
    items.forEach((zone) => {
      if (zone.id) zonesById.set(zone.id, zone);
    });
    token = nextToken;
  } while (token);
  return zonesById;
};

const loadAllSalemen = async (): Promise<Map<string, SalemanDataType>> => {
  const salemenById = new Map<string, SalemanDataType>();
  let token: string | undefined = undefined;
  do {
    const { items, nextToken } = await SalemanApi.fetchAll({}, token, 1000, null, { noCache: true });
    items.forEach((saleman) => {
      if (saleman.id) salemenById.set(saleman.id, saleman);
    });
    token = nextToken;
  } while (token);
  return salemenById;
};

const loadAllClients = async (): Promise<ClientDataType[]> => {
  const all: ClientDataType[] = [];
  let token: string | undefined = undefined;
  do {
    const { items, nextToken } = await ClientApi.fetchAll({}, token, 1000, null, { noCache: true });
    all.push(...items);
    token = nextToken;
  } while (token);

  // Client no tiene relacion belongsTo con Zone ni con Saleman (el schema de Amplify
  // solo guarda los ids), asi que los nombres se resuelven aqui con un join en memoria.
  const [zonesById, salemenById] = await Promise.all([loadAllZones(), loadAllSalemen()]);
  all.forEach((client) => {
    if (client.zoneId) {
      client.zone = zonesById.get(client.zoneId) ?? null;
    }
    if (client.salemanId) {
      client.saleman = salemenById.get(client.salemanId) ?? null;
    }
  });

  return all;
};

const ClientsReportOffCanvas = (props: ClientsReportOffCanvasProps) => {
  const [isOpen, setOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = OpenClientsReportPubSub.subscribe(openOffCanvas);
    return () => {
      unsubscribe();
    };
  }, []);

  const openOffCanvas = async (payload: OpenClientsReportPubSubPayload) => {
    const { openOffCanvas = false } = payload;
    setOpen(openOffCanvas);
  };

  const onCanvasClosed = () => {
    setOpen(false);
  };

  return (
    <DefaultOffCanvas
      className={styles.offCanvas}
      direction="end"
      isOpen={isOpen}
      onClosed={onCanvasClosed}
      toggle={() => setOpen(!isOpen)}
    >
      <OffcanvasHeader className={styles.offCanvasHeader} toggle={onCanvasClosed}>
        Reporte de Clientes
      </OffcanvasHeader>
      <OffcanvasBody>
        <Container>
          <EntityReportForm<ClientDataType>
            columns={clientColumns}
            loadRecords={loadAllClients}
            fileName="clientes.xlsx"
          />
        </Container>
      </OffcanvasBody>
    </DefaultOffCanvas>
  );
};

export default ClientsReportOffCanvas;
